import { createTheme } from "@mui/material/styles";
import { THEME } from "./modules/common/styles/theme";
import { TYPOGRAPHY } from "./modules/common/styles/typography";

const getTheme = (mode, customPrimaryColor = null) =>
  createTheme({
    palette: {
      mode,
      primary: {
        main: customPrimaryColor || THEME[mode].primary,
      },
      secondary: {
        main: THEME[mode].secondary,
      },
      success: {
        main: THEME[mode].success,
      },
      error: {
        main: THEME[mode].danger,
      },
      warning: {
        main: THEME[mode].warning,
      },
      info: {
        main: THEME[mode].primary,
      },
      background: {
        default: THEME[mode].page,
      },
      divider: THEME[mode].border.primary,
      text: {
        primary: THEME[mode].text.primary,
        secondary: THEME[mode].text.secondary,
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      fontSize: 14,
      h1: {
        ...TYPOGRAPHY.H1,
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      },
      h2: {
        ...TYPOGRAPHY.H2,
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      },
      h3: {
        ...TYPOGRAPHY.H3,
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      },
      h4: {
        ...TYPOGRAPHY.H4,
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      },
      bodyLg: {
        ...TYPOGRAPHY.bodyLg,
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      },
      bodyMd: {
        ...TYPOGRAPHY.bodyMd,
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      },
      bodySm: {
        ...TYPOGRAPHY.bodySm,
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: "4px",
            padding: "10px 16px",
            textTransform: "none",
            "&.MuiButton-containedPrimary": {
              boxShadow: "none",
              "&:hover": {
                boxShadow: "none",
              },
              "&:active": {
                boxShadow: "none",
              },
              "&:focus": {
                boxShadow: "none",
              },
            },
            "&.MuiButton-containedSecondary": {
              boxShadow: "none",
              "&:hover": {
                boxShadow: "none",
              },
              "&:active": {
                boxShadow: "none",
              },
              "&:focus": {
                boxShadow: "none",
              },
            },
            "&.MuiButton-containedError": {
              boxShadow: "none",
              "&:hover": {
                boxShadow: "none",
              },
              "&:active": {
                boxShadow: "none",
              },
              "&:focus": {
                boxShadow: "none",
              },
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: ({ theme }) => ({
            textTransform: "none",
            fontWeight: 500,
            fontSize: "14px",
            minHeight: 48,
            minWidth: "auto",
            paddingLeft: 0,
            paddingRight: 0,
            marginRight: "32px",
            color: theme.palette.text.primary,
            "&.Mui-selected": {
              color: theme.palette.primary.main,
            },
          }),
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            "& .MuiTabs-indicator": {
              backgroundColor: "main.primary",
              height: 3,
            },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            borderRadius: "4px",
            padding: "10px 16px",
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            fontSize: "14px",
            color: THEME[mode].text.primary,
          },
          secondary: {
            fontSize: "12px",
            color: THEME[mode].text.secondary,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: THEME[mode].border.secondary,
            borderWidth: ".5px !important",
            borderStyle: "solid",
            boxShadow: "none",
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: "var(--card-border-radius, 8px)",
            borderColor: THEME[mode].border.primary,
            borderWidth: "var(--card-border-width, 1px)",
            borderStyle: "var(--card-border-style, solid)",
            boxShadow: "var(--card-box-shadow, none)",
            backgroundColor: "var(--card-bg, transparent)",
            backgroundImage: "var(--card-bg-image, none)",
            backdropFilter: "var(--card-backdrop-filter, none)",
            transition: "box-shadow 0.2s ease, background-color 0.2s ease",
          },
        },
        variants: [
          {
            props: { variant: "outlined" },
            style: {
              backgroundColor: "var(--card-bg, transparent)",
            },
          },
        ],
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: THEME[mode].pageSecondary,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            backgroundColor: THEME[mode].pageSecondary,
            fontWeight: 600,
          },
          root: {
            borderLeft: "none",
            borderRight: "none",
            borderBottom: `1px solid ${THEME[mode].border.primary}`,
            borderTop: "none",
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            "& .MuiTableRow-root": {
              "& .MuiTableCell-root:first-of-type": {
                borderLeft: "none",
              },
              "& .MuiTableCell-root:last-of-type": {
                borderRight: "none",
              },
            },
            "& .MuiTableHead-root .MuiTableRow-root": {
              borderTop: "none",
            },
            "& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root":
              {
                borderBottom: "none",
              },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: "8px",
            boxShadow:
              mode === "light"
                ? "0px 4px 20px rgba(0, 0, 0, 0.08)"
                : "0px 4px 20px rgba(0, 0, 0, 0.3)",
            border: `1px solid ${mode === "light" ? "#e8e8e8" : "#303030"}`,
            minWidth: "200px",
            padding: "0",
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          sizeSmall: {
            "@media (pointer: coarse)": {
              minWidth: 44,
              minHeight: 44,
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            padding: "12px 16px",
            fontSize: "14px",
            fontWeight: 400,
            minHeight: "auto",
            "@media (pointer: coarse)": {
              minHeight: 48,
            },
            "&:hover": {
              backgroundColor: mode === "light" ? "#f5f5f5" : "#2a2a2a",
            },
            "& .MuiListItemIcon-root": {
              minWidth: "32px",
              color: THEME[mode].text.primary,
            },
            "& .MuiSvgIcon-root": {
              fontSize: "18px",
            },
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            borderRadius: "8px",
            boxShadow:
              mode === "light"
                ? "0px 4px 20px rgba(0, 0, 0, 0.08)"
                : "0px 4px 20px rgba(0, 0, 0, 0.3)",
            border: `1px solid ${mode === "light" ? "#e8e8e8" : "#303030"}`,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme }) => ({
            borderRadius: "8px",
            backgroundColor:
              mode === "light"
                ? "rgba(255, 255, 255, 0.66)"
                : "rgba(68, 68, 68, 0.30)",
            backdropFilter:
              mode === "light"
                ? "blur(5px) saturate(180%)"
                : "blur(15px) saturate(180%)",
            backgroundImage:
              mode === "light"
                ? `linear-gradient(180deg, ${theme.palette.primary.main}0F 0%, ${theme.palette.primary.main}0A 40%, ${theme.palette.primary.main}05 100%)`
                : `linear-gradient(180deg, ${theme.palette.primary.main}08 0%, ${theme.palette.primary.main}04 40%, ${theme.palette.primary.main}02 100%)`,
            border: `1px solid ${mode === "light" ? "rgba(229, 231, 235, 0.4)" : "rgba(60, 60, 60, 0.4)"}`,
            boxShadow:
              mode === "light"
                ? "0px 8px 32px rgba(0, 0, 0, 0.12)"
                : "0px 8px 32px rgba(0, 0, 0, 0.5)",
          }),
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontSize: TYPOGRAPHY.H2.fontSize,
            fontWeight: TYPOGRAPHY.H2.fontWeight,
            lineHeight: TYPOGRAPHY.H2.lineHeight,
          },
        },
      },
    },
  });

export default getTheme;
export const getMuiTheme = getTheme; // Export with the name expected by MUIThemeWrapper
